import {
  isConditionalPrefillEditMode,
  tableHasTableConditionalEdit
} from '../editForm/prefillFieldSettingsUtils'
import {
  buildTableColumnConditionalEditFieldId,
  buildTableRowConditionalEditFieldId,
  defaultColumnHeaders,
  defaultRowHeaders,
  getTableDimensions,
  normalizePrefillEditFieldIds,
  resolvePrefillEditFieldId,
  resizeHeaders
} from '../editForm/tableFieldUtils'

const normalizeSubmissionFormValue = (
  formValue: unknown
): Array<{ fieldId?: number; id?: number; label?: string; value?: unknown }> => {
  if (!formValue) return []
  let entries: unknown = formValue
  if (typeof formValue === 'string') {
    try {
      entries = JSON.parse(formValue)
    } catch {
      return []
    }
  }
  return Array.isArray(entries) ? entries : []
}

export interface PrefillFieldEdit {
  fieldId: number
  label: string
  originalValue: string
  submittedValue: string
  status: 'pending' | 'approved' | 'rejected'
  editType?: 'main' | 'conditional'
  scope?: 'field' | 'table_row'
  tableFieldId?: number
  rowIndex?: number
  rowHeader?: string
  parentFieldId?: number
  parentLabel?: string
  parentOriginalValue?: string
  editFieldLabel?: string
  reviewedAt?: string
  remark?: string
}

export const buildConditionalEditSubmissionLabel = (
  parentLabel: string,
  editFieldLabel: string,
  parentOriginalValue?: string
): string => {
  const parentHeading = parentOriginalValue
    ? `${parentLabel} (${parentOriginalValue})`
    : parentLabel
  return `${parentHeading} — ${editFieldLabel}`
}

export const buildConditionalEditParentHeading = (edit: PrefillFieldEdit): string => {
  if (!edit.parentLabel) return edit.parentOriginalValue || ''
  if (edit.parentOriginalValue) {
    return `${edit.parentLabel} (${edit.parentOriginalValue})`
  }
  return edit.parentLabel
}

export const getConditionalEditOriginalDisplay = (edit: PrefillFieldEdit): string => {
  if (edit.editType !== 'conditional') return edit.originalValue
  const parentHeading = buildConditionalEditParentHeading(edit)
  if (parentHeading) return parentHeading

  const labelParts = edit.label.split(' — ')
  if (labelParts.length >= 2) {
    const embeddedParentHeading = labelParts.slice(0, -1).join(' — ')
    if (embeddedParentHeading.includes('(')) {
      return embeddedParentHeading
    }
    if (edit.parentLabel) return edit.parentLabel
  }

  return edit.originalValue
}

export const getConditionalEditSubmittedDisplay = (edit: PrefillFieldEdit): string => {
  if (edit.editType !== 'conditional') return edit.submittedValue
  const fieldLabel = edit.editFieldLabel || edit.label.split(' — ').pop() || 'Value'
  if (!edit.submittedValue) return ''
  return `${fieldLabel} (Person edited as — ${edit.submittedValue})`
}

const flattenFormPages = (formData: unknown): any[] => {
  if (!formData) return []
  let pages = Array.isArray(formData) ? formData : []
  if (pages.length > 0 && !Array.isArray(pages[0])) {
    pages = [pages]
  }
  return pages.flat().filter(Boolean)
}

export interface ConditionalEditColumnMeta {
  fieldId: number
  label: string
  parentFieldId: number
  parentLabel: string
  editFieldLabel: string
  editFieldType?: string
  /** Present for table row / table column scoped conditional edits. */
  rowIndex?: number
}

export const extractConditionalEditColumnsFromForm = (
  formData: unknown
): ConditionalEditColumnMeta[] => {
  const columns: ConditionalEditColumnMeta[] = []

  flattenFormPages(formData).forEach((element) => {
    const parentFieldId = Number(element.fieldId ?? element.id)
    const parentLabel = String(element.label || '').trim()
    if (!parentFieldId || !parentLabel) return

    const isTable = element.type === 'table'
    const hasConditional = isTable
      ? tableHasTableConditionalEdit(element)
      : isConditionalPrefillEditMode(element)
    if (!hasConditional) return

    if (isTable) {
      const { rows, cols } = getTableDimensions(element)
      const rowHeaders = resizeHeaders(element.rowHeaders, rows, defaultRowHeaders)
      const columnHeaders = resizeHeaders(element.columnHeaders, cols, defaultColumnHeaders)
      const rowSettings: Array<{
        allowEdit?: boolean
        prefillEditFields?: Array<{ fieldId: number; label: string; type?: string }>
      }> = element.tableRowPrefillSettings || []
      const columnSettings: Array<{
        prefillEditFields?: Array<{ fieldId: number; label: string; type?: string }>
      }> = element.tableColumnPrefillSettings || []

      rowSettings.forEach((rowConfig, rowIndex) => {
        if (rowConfig?.allowEdit !== true) return
        const rowEditFields = rowConfig.prefillEditFields || []
        if (rowEditFields.length === 0) return

        const rowHeader = rowHeaders[rowIndex] || `Row ${rowIndex + 1}`
        const rowParentHeading = `${parentLabel} — ${rowHeader}`

        rowEditFields.forEach((editField, editFieldIndex) => {
          const editFieldId = resolvePrefillEditFieldId(editField, editFieldIndex)
          const editFieldLabel = String(editField.label || '').trim()
          if (!editFieldLabel) return

          columns.push({
            fieldId: buildTableRowConditionalEditFieldId(parentFieldId, rowIndex, editFieldId),
            label: buildConditionalEditSubmissionLabel(rowParentHeading, editFieldLabel),
            parentFieldId,
            parentLabel,
            editFieldLabel,
            editFieldType: editField.type,
            rowIndex
          })
        })
      })

      for (let rowIndex = 0; rowIndex < rows; rowIndex += 1) {
        const rowHeader = rowHeaders[rowIndex] || `Row ${rowIndex + 1}`
        for (let colIndex = 0; colIndex < cols; colIndex += 1) {
          const columnEditFields = columnSettings[colIndex]?.prefillEditFields || []
          if (columnEditFields.length === 0) continue

          const columnHeader = columnHeaders[colIndex] || `Column ${colIndex + 1}`
          const rowParentHeading = `${parentLabel} — ${rowHeader} — ${columnHeader}`

          columnEditFields.forEach((editField, editFieldIndex) => {
            const editFieldId = resolvePrefillEditFieldId(editField, editFieldIndex)
            const editFieldLabel = String(editField.label || '').trim()
            if (!editFieldLabel) return

            columns.push({
              fieldId: buildTableColumnConditionalEditFieldId(
                parentFieldId,
                rowIndex,
                colIndex,
                editFieldId
              ),
              label: buildConditionalEditSubmissionLabel(rowParentHeading, editFieldLabel),
              parentFieldId,
              parentLabel,
              editFieldLabel,
              editFieldType: editField.type,
              rowIndex
            })
          })
        }
      }
    }

    ;(element.prefillEditFields || []).forEach((editField: any, editFieldIndex: number) => {
      const fieldId = resolvePrefillEditFieldId(editField, editFieldIndex, parentFieldId)
      const editFieldLabel = String(editField.label || '').trim()
      if (!fieldId || !editFieldLabel) return

      if (columns.some((column) => column.fieldId === fieldId)) return

      columns.push({
        fieldId,
        label: buildConditionalEditSubmissionLabel(parentLabel, editFieldLabel),
        parentFieldId,
        parentLabel,
        editFieldLabel,
        editFieldType: editField.type
      })
    })
  })

  const seenFieldIds = new Set<number>()
  return columns.filter((column) => {
    if (seenFieldIds.has(column.fieldId)) return false
    seenFieldIds.add(column.fieldId)
    return true
  })
}

const findSubmissionEntryForConditionalField = (
  formValue: Array<{ fieldId?: number; id?: number; label?: string; value?: unknown }>,
  fieldId: number,
  parentLabel: string,
  editFieldLabel: string,
  parentOriginalValue?: string
) => {
  const labelCandidates = [
    buildConditionalEditSubmissionLabel(parentLabel, editFieldLabel),
    buildConditionalEditSubmissionLabel(parentLabel, editFieldLabel, parentOriginalValue),
    `${parentLabel} — Edit: ${editFieldLabel}`
  ].filter(Boolean)

  // Prefer label match first so colliding top-level fieldIds cannot steal conditional values.
  for (const label of labelCandidates) {
    const byLabel = formValue.find(
      (entry) => entry.label === label && String(entry.value ?? '').trim()
    )
    if (byLabel) return byLabel
  }

  const suffix = ` — ${editFieldLabel}`
  const bySuffix = formValue.find((entry) => {
    const label = String(entry.label || '')
    return (
      label.endsWith(suffix) &&
      label.startsWith(parentLabel) &&
      Boolean(String(entry.value ?? '').trim())
    )
  })
  if (bySuffix) return bySuffix

  const byId = formValue.find(
    (entry) =>
      Number(entry.fieldId ?? entry.id) === Number(fieldId) &&
      String(entry.label || '').includes(' — ') &&
      String(entry.value ?? '').trim()
  )
  if (byId) return byId

  return undefined
}

/** Recover conditional edits present in formValue but missing from stored prefillFieldEdits. */
export const enrichPrefillFieldEditsFromSubmission = (
  edits: PrefillFieldEdit[] | undefined | null,
  formData: unknown,
  formValue: unknown
): PrefillFieldEdit[] => {
  // Clone objects — Apollo cache entries are frozen and cannot be mutated.
  const existing: PrefillFieldEdit[] = Array.isArray(edits)
    ? edits.map((edit) => ({ ...edit }))
    : []
  const entries = normalizeSubmissionFormValue(formValue)
  if (!formData || entries.length === 0) return existing

  const existingKeys = new Set(
    existing.map(
      (edit) =>
        `${edit.editType || 'main'}:${edit.parentFieldId || ''}:${edit.rowIndex ?? ''}:${
          edit.editFieldLabel || edit.label || edit.fieldId
        }`
    )
  )

  flattenFormPages(formData).forEach((element) => {
    const parentFieldId = Number(element.fieldId ?? element.id)
    const parentLabel = String(element.label || '').trim()
    if (!parentFieldId || !parentLabel) return

    const siblingEdit = existing.find(
      (edit) => edit.editType === 'conditional' && Number(edit.parentFieldId) === parentFieldId
    )
    // Prefer label-scoped main formValue entry so duplicate fieldIds cannot steal Previous.
    const parentMainEntry = entries.find(
      (entry) =>
        String(entry.label || '').trim() === parentLabel &&
        !String(entry.label || '').includes(' — ')
    )
    const fromFormValue = String(parentMainEntry?.value ?? '').trim()
    const fromSibling = String(siblingEdit?.parentOriginalValue ?? '').trim()
    // Prefer stored parentOriginalValue from detect (computed on raw submit before formValue
    // may be polluted by bare-fieldId restore). formValue is fallback only.
    const parentOriginalValue = fromSibling || fromFormValue

    const pushIfMissing = (edit: PrefillFieldEdit) => {
      const key = `${edit.editType || 'main'}:${edit.parentFieldId || ''}:${edit.rowIndex ?? ''}:${
        edit.editFieldLabel || edit.label
      }`
      if (existingKeys.has(key)) return
      // Identity is parent + edit-field label (and row), NOT bare fieldId.
      // Rejecting on fieldId alone prevented recovering text when it shared an id with a file edit.
      if (
        existing.some(
          (item) =>
            item.editType === 'conditional' &&
            Number(item.parentFieldId) === Number(edit.parentFieldId) &&
            item.editFieldLabel === edit.editFieldLabel &&
            item.rowIndex === edit.rowIndex
        )
      ) {
        return
      }
      existingKeys.add(key)
      existing.push(edit)
    }

    if (element.type === 'table') {
      if (!tableHasTableConditionalEdit(element)) return
      const { rows } = getTableDimensions(element)
      const rowHeaders = resizeHeaders(element.rowHeaders, rows, defaultRowHeaders)
      const rowSettings: Array<{
        allowEdit?: boolean
        prefillEditFields?: Array<{ fieldId: number; label: string; type?: string }>
      }> = element.tableRowPrefillSettings || []

      rowSettings.forEach((rowConfig, rowIndex) => {
        if (rowConfig?.allowEdit !== true) return
        const rowEditFields = rowConfig.prefillEditFields || []
        if (rowEditFields.length === 0) return

        const rowHeader = rowHeaders[rowIndex] || `Row ${rowIndex + 1}`
        const rowParentHeading = `${parentLabel} — ${rowHeader}`
        const status =
          existing.find(
            (edit) =>
              edit.scope === 'table_row' &&
              Number(edit.tableFieldId) === parentFieldId &&
              edit.rowIndex === rowIndex
          )?.status ||
          siblingEdit?.status ||
          'pending'

        rowEditFields.forEach((editField, editFieldIndex) => {
          const editFieldId = resolvePrefillEditFieldId(editField, editFieldIndex)
          const editFieldLabel = String(editField.label || '').trim()
          if (!editFieldLabel) return

          const syntheticFieldId = buildTableRowConditionalEditFieldId(
            parentFieldId,
            rowIndex,
            editFieldId
          )
          const entry = findSubmissionEntryForConditionalField(
            entries,
            syntheticFieldId,
            rowParentHeading,
            editFieldLabel
          )
          const submittedValue = String(entry?.value ?? '').trim()
          if (!submittedValue) return

          pushIfMissing({
            fieldId: syntheticFieldId,
            label: buildConditionalEditSubmissionLabel(rowParentHeading, editFieldLabel),
            originalValue: '',
            submittedValue,
            status,
            editType: 'conditional',
            scope: 'table_row',
            tableFieldId: parentFieldId,
            rowIndex,
            rowHeader,
            parentFieldId,
            parentLabel,
            parentOriginalValue: siblingEdit?.parentOriginalValue || '',
            editFieldLabel
          })
        })
      })
      return
    }

    if (!isConditionalPrefillEditMode(element)) return
    const editFields = normalizePrefillEditFieldIds(
      Array.isArray(element.prefillEditFields) ? element.prefillEditFields : [],
      parentFieldId
    )
    if (editFields.length === 0) return

    const status = siblingEdit?.status || 'pending'

    editFields.forEach((editField: any, editFieldIndex: number) => {
      const editFieldId = Number(editField.fieldId)
      const editFieldLabel = String(editField.label || '').trim()
      if (!editFieldId || !editFieldLabel) return

      const entry = findSubmissionEntryForConditionalField(
        entries,
        editFieldId,
        parentLabel,
        editFieldLabel,
        parentOriginalValue || undefined
      )
      const submittedValue = String(entry?.value ?? '').trim()
      if (!submittedValue) return

      pushIfMissing({
        fieldId: editFieldId,
        label: buildConditionalEditSubmissionLabel(parentLabel, editFieldLabel),
        originalValue: '',
        submittedValue,
        status,
        editType: 'conditional',
        parentFieldId,
        parentLabel,
        parentOriginalValue,
        editFieldLabel
      })
    })
  })

  // Only fill missing Previous values — never overwrite a stored parentOriginalValue
  // with formValue (formValue may have been poisoned by bare-fieldId restore).
  return existing.map((edit) => {
    if (edit.editType !== 'conditional' || !edit.parentLabel) return edit
    if (String(edit.parentOriginalValue ?? '').trim()) return edit
    const parentMainEntry = entries.find(
      (entry) =>
        String(entry.label || '').trim() === edit.parentLabel &&
        !String(entry.label || '').includes(' — ')
    )
    const corrected = String(parentMainEntry?.value ?? '').trim()
    if (!corrected) return edit
    return { ...edit, parentOriginalValue: corrected }
  })
}

export const getPrefillEditForField = (
  edits: PrefillFieldEdit[] | undefined | null,
  fieldId: number
): PrefillFieldEdit | undefined =>
  (edits || []).find((edit) => Number(edit.fieldId) === Number(fieldId))

export const getPrefillEditForTableCell = (
  edits: PrefillFieldEdit[] | undefined | null,
  tableFieldId: number,
  rowIndex: number
): PrefillFieldEdit | undefined =>
  (edits || []).find(
    (edit) =>
      edit.scope === 'table_row' &&
      // Conditional extras (e.g. file upload) have their own columns — do not
      // overwrite table cell values (Asset Type / Name / Remarks) with them.
      edit.editType !== 'conditional' &&
      Number(edit.tableFieldId) === Number(tableFieldId) &&
      edit.rowIndex === rowIndex
  )

export const formatEditTypeLabel = (edit: PrefillFieldEdit): string => {
  if (edit.scope === 'table_row' && edit.editType === 'conditional') {
    return 'Table row conditional'
  }
  if (edit.scope === 'table_row') return 'Table row'
  return edit.editType === 'conditional' ? 'Conditional field' : 'Prefilled field'
}

export const getEditHighlightClass = (status?: PrefillFieldEdit['status']): string => {
  switch (status) {
    case 'pending':
      return 'submission-cell-edited submission-cell-edited--pending'
    case 'approved':
      return 'submission-cell-edited submission-cell-edited--approved'
    case 'rejected':
      return 'submission-cell-edited submission-cell-edited--rejected'
    default:
      return ''
  }
}

export const formatEditReviewStatus = (edits: PrefillFieldEdit[] | undefined | null): string => {
  const list = edits || []
  if (list.length === 0) return 'No edits'

  const pending = list.filter((edit) => edit.status === 'pending').length
  const approved = list.filter((edit) => edit.status === 'approved').length
  const rejected = list.filter((edit) => edit.status === 'rejected').length

  const parts: string[] = []
  if (pending > 0) parts.push(`${pending} pending`)
  if (approved > 0) parts.push(`${approved} approved`)
  if (rejected > 0) parts.push(`${rejected} rejected`)
  return parts.join(', ')
}
